import React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Party } from "@/app/types/party";
import { Button } from "@/components/ui/button";
import { MoreHorizontal, Eye } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";

// Helper function to format currency
const formatCurrency = (amount: string | number): string => {
  const value = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(value)) return '0.00';
  return value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Define the extended column type with meta
interface CustomColumnMeta {
  label?: string;
  placeholder?: string;
  variant?: 'text' | 'number' | 'select' | 'date' | 'dateRange' | 'boolean' | 'multiSelect';
  icon?: React.ComponentType<{ className?: string }>;
  options?: Array<{ label: string; value: string | number | boolean }>;
}

type ExtendedColumnDef<TData, TValue = unknown> = ColumnDef<TData, TValue> & {
  meta?: CustomColumnMeta;
};

// Helper component for the table header
export const DataTableColumnHeader = ({
  column,
  title,
}: {
  column: any;
  title: string;
}) => {
  return (
    <div className="flex items-center space-x-2">
      <span>{title}</span>
      {column.getCanSort() && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => column.toggleSorting()}
        >
          <span className="sr-only">Toggle sort</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4"
          >
            <path d="m3 16 4 4 4-4" />
            <path d="M7 20V4" />
            <path d="m21 8-4-4-4 4" />
            <path d="M17 4v16" />
          </svg>
        </Button>
      )}
    </div>
  );
};

// Define columns for the Client Manager Parties table
export const columns: ExtendedColumnDef<Party>[] = [
  {
    accessorKey: "name",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
    cell: ({ row }) => <div className="font-medium">{row.getValue("name")}</div>,
    enableColumnFilter: true,
    meta: {
      label: "Name",
      placeholder: "Filter by name...",
      variant: "text",
    },
  },
  {
    accessorKey: "phone",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Phone" />,
    cell: ({ row }) => <div>{row.getValue("phone") || "-"}</div>,
    enableColumnFilter: true,
    meta: {
      label: "Phone",
      placeholder: "Filter by phone...",
      variant: "text",
    },
  },
  {
    accessorKey: "email",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Email" />,
    cell: ({ row }) => <div>{row.getValue("email") || "-"}</div>,
    enableColumnFilter: true,
    meta: {
      label: "Email",
      placeholder: "Filter by email...",
      variant: "text",
    },
  },
  {
    accessorKey: "party_type",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Type" />,
    cell: ({ row }) => {
      const partyType = row.getValue("party_type") as string | null;
      return (
        <Badge variant={partyType === "customer" ? "default" : "secondary"}>
          {partyType || "Not Specified"}
        </Badge>
      );
    },
    enableColumnFilter: true,
    meta: {
      label: "Party Type",
      placeholder: "Select type...",
      variant: "select",
      options: [
        { label: "Customer", value: "customer" },
        { label: "Vendor", value: "vendor" },
      ],
    },
  },
  {
    accessorKey: "opening_balance",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Opening Balance" />,
    cell: ({ row }) => {
      const balance = row.getValue("opening_balance") as number | null;
      const balanceType = row.original.opening_balance_type;

      if (balance === null || balance === undefined) {
        return <div className="text-muted-foreground">-</div>;
      }

      return (
        <div className={balance < 0 ? "text-red-600" : "text-green-600"}>
          {formatCurrency(Math.abs(balance))} {balanceType === "credit" ? "CR" : "DR"}
        </div>
      );
    },
  },
  {
    id: "actions",
    cell: ({ row }) => {
      const party = row.original;

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={() => document.dispatchEvent(
                new CustomEvent("view-party", { detail: party })
              )}
            >
              <Eye className="mr-2 h-4 w-4" />
              <span>View Details</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
